import { createDemoStore } from '@/demo/core/create-demo-store';
import { autoResolvedSeed } from './auto-resolved';
import type { InvoiceId } from './invoices-data';

/**
 * Mutable state for the invoice compliance page: which invoices have been
 * resolved, the supplier follow-up thread, and edited email drafts. Resets on
 * page reload.
 */

export interface EmailDraft {
  to: string;
  cc: string[];
  subject: string;
  body: string;
}

export interface ThreadMessage {
  id: string;
  from: 'agent' | 'supplier' | 'system';
  author: string;
  body: string;
  /** Simulated time since the notice went out, e.g. "3 hours later". */
  whenLabel: string;
  /** The email as sent, shown under the first message. */
  email?: EmailDraft;
}

export type ResolutionKind = 'recommendation' | 'override';

/** 0: notice sent, 1: supplier acknowledged, 2: corrected document verified. */
export type FollowUpStage = 0 | 1 | 2;

export interface Resolution {
  kind: ResolutionKind;
  /** Epoch milliseconds. */
  at: number;
  stage: FollowUpStage;
  override?: { reason: string; note: string; by: string };
  /** The agent resolved it on its own at ingestion, so it cannot be reopened. */
  auto?: boolean;
}

interface ComplianceState {
  resolutions: Partial<Record<InvoiceId, Resolution>>;
  threads: Partial<Record<InvoiceId, ThreadMessage[]>>;
  drafts: Partial<Record<InvoiceId, EmailDraft>>;
}

function buildSeed(): ComplianceState {
  const { resolutions, threads } = autoResolvedSeed();
  return { resolutions, threads, drafts: {} };
}

export const complianceStore = createDemoStore(buildSeed);

export function resetComplianceDemo(): void {
  complianceStore.reset();
}

export function getResolution(id: InvoiceId): Resolution | undefined {
  return complianceStore.getState().resolutions[id];
}

export function recordResolution(
  id: InvoiceId,
  resolution: Resolution,
  messages: ThreadMessage[],
): void {
  complianceStore.update((draft) => {
    draft.resolutions[id] = resolution;
    draft.threads[id] = messages;
  });
}

export function reopenInvoice(id: InvoiceId): void {
  complianceStore.update((draft) => {
    delete draft.resolutions[id];
    delete draft.threads[id];
  });
}

/** Append thread messages and advance the follow-up stage. */
export function advanceFollowUp(
  id: InvoiceId,
  stage: FollowUpStage,
  messages: ThreadMessage[],
): void {
  complianceStore.update((draft) => {
    const resolution = draft.resolutions[id];
    if (!resolution) return;
    resolution.stage = stage;
    draft.threads[id] = [...(draft.threads[id] ?? []), ...messages];
  });
}

export function saveDraft(id: InvoiceId, email: EmailDraft): void {
  complianceStore.update((draft) => {
    draft.drafts[id] = email;
  });
}

export function useResolution(id: InvoiceId): Resolution | undefined {
  return complianceStore.useStore((state) => state.resolutions[id]);
}

export function useResolutions(): Partial<Record<InvoiceId, Resolution>> {
  return complianceStore.useStore((state) => state.resolutions);
}

export function useThread(id: InvoiceId): ThreadMessage[] {
  return complianceStore.useStore((state) => state.threads[id] ?? []);
}

export function useDraft(id: InvoiceId): EmailDraft | undefined {
  return complianceStore.useStore((state) => state.drafts[id]);
}
